import { Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Depot, Thing } from '../../api/types';
import Card from '../../components/Card';
import CheckCircleIcon from '../../components/icons/CheckCircleIcon';
import CrossCircleIcon from '../../components/icons/CrossCircleIcon';
import TicketIcon from '../../components/icons/TicketIcon';
import SignalIcon from '../../components/icons/SignalIcon';
import { tenantSelector } from '../../selector/authenticationSelector';
import { formatAddressLine } from '../../utils/addressLine';

interface DepotCardProps {
    depot: DepotWithAddress;
    onPress: () => void;
    thing?: Thing;
}

export interface DepotWithAddress extends Depot {
    /**
     * Optional in truth: `location.address_id` is nullable and every location
     * created by the station wizard before #2433 has none. The old declaration
     * claimed it was always present, which is why the card rendered the literal
     * string "undefined, " — `undefined + ', '` concatenates, and optional
     * chaining does not protect against that.
     */
    address?: {
        street?: string;
        /** On the wire since forever — `processPGData` camel-cases
         *  `address.house_number`, which is read without a `select`. It was
         *  simply never declared here, so the card showed "Teststraße, 04103
         *  Leipzig" for Teststraße 7 (QA #2439, defect D). */
        houseNumber?: string;
        postalCode?: string;
        city?: string;
    };
}

/**
 * One line of address, or the missing-value marker.
 *
 * The line itself is built by `utils/addressLine.ts` — one rule for every
 * surface that shows a station's address. This adds only the fallback, which is
 * this card's own answer to "no address at all".
 */
export const formatDepotAddress = (
    address: DepotWithAddress['address'],
    fallback: string,
): string => formatAddressLine(address) || fallback;

const DepotCard = (props: DepotCardProps) => {
    const selectedTenant = useSelector(tenantSelector);
    const { t } = useTranslation();

    const isEnabled =
        selectedTenant === 'segmenta'
            ? props.depot.enabled
            : props.depot.status?.enabled;

    const onlineStatus = props.thing?.minutesFromLastSeen !== undefined
        ? props.thing.minutesFromLastSeen <= 10
            ? 'online'
            : 'offline'
        : 'unknown';

    const onlineTooltip = {
        online: t('depots.status.online'),
        offline: t('depots.status.offline'),
        unknown: t('depots.status.unknown'),
    }[onlineStatus];

    const onlineColor = {
        online: 'text-[#27AE60]',
        offline: 'text-[#E72525]',
        unknown: 'text-gray-400',
    }[onlineStatus];

    return (
        <Card onClick={props.onPress}>
            <div className={'flex flex-col grow min-w-0'}>
                <h2
                    className={
                        'truncate text-lg font-semibold -tracking-[0.0225rem] text-foreground text-left'
                    }
                >
                    {props.depot.name}
                </h2>
                <span
                    className={
                        'text-foreground opacity-60 text-sm truncate text-left'
                    }
                >
                    {formatDepotAddress(props.depot.address, t('common.not_applicable'))}
                </span>
            </div>
            <div className={'flex flex-row gap-1 shrink-0 items-center'}>
                <Tooltip title={isEnabled ? t('depots.status.enabled') : t('depots.status.disabled')}>
                    <span>{isEnabled ? <CheckCircleIcon /> : <CrossCircleIcon />}</span>
                </Tooltip>
                <Tooltip title={props.depot.status?.bookable ? t('depots.status.bookable') : t('depots.status.not_bookable')}>
                    <span>
                        {props.depot.status?.bookable ? (
                            <TicketIcon className={'text-[#27AE60]'} />
                        ) : (
                            <TicketIcon className={'text-[#E72525]'} />
                        )}
                    </span>
                </Tooltip>
                <Tooltip title={onlineTooltip}>
                    <span>
                        <SignalIcon className={onlineColor} />
                    </span>
                </Tooltip>
            </div>
        </Card>
    );
};

export default DepotCard;
